/**
 * Song timeline: the song unrolled into the bars it plays, in order, each
 * bar with the slots it holds and where they start. Times are in quarter
 * notes, so tempo only scales them (seconds = quarters × 60 / tempo).
 *
 * A bar's `slots` are what plays: a split bar changes chord inside the bar
 * at the slot boundaries `layoutBarSlots` works out. A bar also carries
 * `slotId`, `kind` and `chord` of its first slot, so consumers that still
 * think one chord per bar keep working unchanged for unsplit bars.
 *
 * Rules:
 * - Meter: the global time signature when set (rows carry none then).
 *   Otherwise, as in written music, a row's own signature stays in force
 *   until a later row sets another, in playback order: across sections and
 *   through repeat passes. A repeated section's second pass starts in the
 *   meter in force at the end of the first, so a section that changes meter
 *   inside needs a signature on its first row to restart in its opening
 *   meter. Rows before the first signature play in 4/4.
 * - Section repeats unroll (`getSectionRepeat`); each bar records its pass.
 * - Playback plays what's written. A slot with a chord plays it.
 * - A slot with custom text ("N.C.", "Tacet") is a silent bar, even when it
 *   also has a chord (the text is what's shown).
 * - A slot with no chord (empty, or lyrics only) is a rest: nothing sounds.
 *   There is no implicit hold; a chord that keeps going is written again.
 * - Rows with nothing in them play too, as rests: the editor shows each of
 *   their slots with the silence mark. (Performance View hides them; the spotlight
 *   stays on the pair before.)
 * - Rest slots at the very end of the song are trimmed (a bar left with no
 *   slots goes too, one left shorter plays shorter).
 */
import { DEFAULT_TIME_SIGNATURE, layoutBarSlots } from './barBeats.js';
import { normalizeSongTimeSignature } from './songMeta.js';
import { getSectionRepeat } from './sectionMarkers.js';
export { DEFAULT_TIME_SIGNATURE };
/**
 * True when nothing gives the song a meter: no global time signature and no
 * row with its own. Playback asks for one rather than assuming 4/4.
 */
export function songNeedsTimeSignature(sections, globalTimeSignature) {
    if (globalTimeSignature) {
        return false;
    }
    return !sections.some((section) => section.rows.some((row) => normalizeSongTimeSignature(row.timeSignature) !== null));
}
/** True when the timeline would strike at least one chord. */
export function timelineHasChords(timeline) {
    return timeline.bars.some((bar) => bar.slots.some((slot) => slot.kind === 'chord'));
}
function classifySlot(slot) {
    if (slot.customText) {
        return { kind: 'silent', chord: null };
    }
    if (slot.chord) {
        return { kind: 'chord', chord: slot.chord };
    }
    return { kind: 'rest', chord: null };
}
/** The slots of one bar laid out in time, from `startQuarter`. */
function timelineSlots(bar, timeSignature, startQuarter) {
    const unitQuarters = 4 / timeSignature.denominator;
    return layoutBarSlots(bar, timeSignature).map((placed, i) => ({
        slotId: placed.slotId,
        startQuarter: startQuarter + placed.startUnits * unitQuarters,
        lengthQuarters: placed.lengthUnits * unitQuarters,
        ...classifySlot(bar.slots[i]),
    }));
}
/** Unrolls the song into the bars it plays. */
export function buildSongTimeline(sections, globalTimeSignature) {
    const bars = [];
    let quarter = 0;
    let rowMeter = DEFAULT_TIME_SIGNATURE;
    for (const section of sections) {
        const passes = getSectionRepeat(section) ?? 1;
        for (let pass = 1; pass <= passes; pass++) {
            for (const row of section.rows) {
                rowMeter = normalizeSongTimeSignature(row.timeSignature) ?? rowMeter;
                const timeSignature = globalTimeSignature ?? rowMeter;
                for (const bar of row.bars) {
                    if (bar.slots.length === 0) {
                        continue;
                    }
                    const slots = timelineSlots(bar, timeSignature, quarter);
                    const lengthQuarters = slots.reduce((sum, slot) => sum + slot.lengthQuarters, 0);
                    bars.push({
                        index: bars.length,
                        sectionId: section.id,
                        rowId: row.id,
                        barId: bar.id,
                        slotId: slots[0].slotId,
                        pass,
                        timeSignature,
                        startQuarter: quarter,
                        lengthQuarters,
                        kind: slots[0].kind,
                        chord: slots[0].chord,
                        slots,
                    });
                    quarter += lengthQuarters;
                }
            }
        }
    }
    // Trim rest slots at the very end: whole rest bars go, a bar that ends in
    // rests plays up to its last sounding slot.
    while (bars.length > 0) {
        const last = bars[bars.length - 1];
        const slots = [...last.slots];
        while (slots.length > 0 && slots[slots.length - 1].kind === 'rest') {
            slots.pop();
        }
        if (slots.length === 0) {
            bars.pop();
            quarter = last.startQuarter;
            continue;
        }
        if (slots.length !== last.slots.length) {
            const end = slots[slots.length - 1];
            quarter = end.startQuarter + end.lengthQuarters;
            bars[bars.length - 1] = { ...last, slots, lengthQuarters: quarter - last.startQuarter };
        }
        break;
    }
    return { bars, totalQuarters: quarter };
}
